import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Loader2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { AppEmptyState, AppPage, AppPageHeader } from '@/components/layout/AppPage';
import {
  MatterCompactAction,
  MatterContinueCard,
  MatterHomeSection,
} from '@/components/matter/MatterHomePanel';

const CLOSED_STAGES = new Set(['closed', 'settled', 'archived']);

function normalizeStage(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
}

function normalizeMatter(item = {}) {
  const matterId = String(item.matter_id || '').trim();
  return {
    matter_id: matterId,
    title: String(item.title || item.name || matterId || 'Matter').trim(),
    summary: String(item.suggested_focus || item.recommended_action || item.summary || item.court_name || item.case_number || 'Continue this matter.').trim(),
    stage: String(item.stage || 'active').trim(),
    stage_key: normalizeStage(item.stage || 'active'),
    priority: String(item.priority || 'normal').trim(),
    health_score: Number(item.health_score || 0),
    next_hearing_date: String(item.next_hearing_date || '').trim(),
    court_name: String(item.court_name || item.court_forum || '').trim(),
    case_number: String(item.case_number || '').trim(),
    continue_href: String(item.route || (matterId ? `/war-room/${matterId}` : '/war-room')).trim(),
    continue_label: 'Continue',
    continue_summary: String(item.suggested_focus || item.recommended_action || item.summary || 'Continue this matter.').trim(),
    continue_kind: 'MATTER',
    continue_source: String(item.stage || 'Matter').trim(),
    has_resume: true,
  };
}

function MatterDashboard() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        ...(options.headers || getAuthHeaders()),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Request failed');
    }
    return data;
  }, [getAuthHeaders]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchJson(`${API}/matters/dashboard/summary`);
      setDashboard(payload);
    } catch (error) {
      toast.error(error.message || 'Unable to load matters');
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    document.title = 'All Matters — Advoq';
    loadDashboard();
  }, [loadDashboard]);

  const urgentMatters = useMemo(
    () => (dashboard?.urgent_matters || []).map(normalizeMatter).slice(0, 4),
    [dashboard?.urgent_matters],
  );
  const allMatters = useMemo(
    () => (dashboard?.matters || []).map(normalizeMatter),
    [dashboard?.matters],
  );
  const openMatters = useMemo(
    () => allMatters.filter((item) => !CLOSED_STAGES.has(item.stage_key)).slice(0, 12),
    [allMatters],
  );
  const archiveMatters = useMemo(
    () => allMatters.filter((item) => CLOSED_STAGES.has(item.stage_key)).slice(0, 8),
    [allMatters],
  );

  return (
    <main className="app-main" data-testid="matter-dashboard-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Matters"
          title="All matters"
          description="Continue the matter that needs attention."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadDashboard} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
        />

        <div style={{ display: 'grid', gap: '16px' }}>
          <MatterHomeSection
            title="Needs attention"
            count={urgentMatters.length}
            action={<button type="button" className="app-chat-button" onClick={() => navigate('/war-room')}>New matter</button>}
          >
            {loading && !dashboard ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: 'var(--text-secondary)' }}>
                <Loader2 size={16} className="animate-spin" />
                Loading matters...
              </div>
            ) : null}

            {!loading && !urgentMatters.length ? (
              <AppEmptyState
                icon={FolderOpen}
                title="No urgent matters"
                description="Continue any open matter below."
              />
            ) : null}

            {urgentMatters.length ? (
              <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                {urgentMatters.map((matter) => (
                  <MatterContinueCard
                    key={`urgent_${matter.matter_id || matter.title}`}
                    matter={matter}
                    onContinue={() => navigate(matter.continue_href)}
                    dataTestId={`urgent-matter-${matter.matter_id || matter.title}`}
                  />
                ))}
              </div>
            ) : null}
          </MatterHomeSection>

          <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <MatterHomeSection title="Open matters" count={openMatters.length}>
              {openMatters.length ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {openMatters.map((matter) => (
                    <MatterCompactAction
                      key={`open_${matter.matter_id || matter.title}`}
                      item={matter}
                      onOpen={() => navigate(matter.continue_href)}
                      dataTestId={`open-matter-${matter.matter_id || matter.title}`}
                    />
                  ))}
                </div>
              ) : (
                <AppEmptyState
                  icon={FolderOpen}
                  title="No open matters"
                  description="Start a new matter to begin."
                />
              )}
            </MatterHomeSection>

            <MatterHomeSection title="Closed or archived" count={archiveMatters.length}>
              {archiveMatters.length ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {archiveMatters.map((matter) => (
                    <MatterCompactAction
                      key={`archive_${matter.matter_id || matter.title}`}
                      item={matter}
                      onOpen={() => navigate(matter.continue_href)}
                      dataTestId={`archive-matter-${matter.matter_id || matter.title}`}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  No closed matters yet.
                </div>
              )}
            </MatterHomeSection>
          </div>
        </div>
      </AppPage>
    </main>
  );
}

MatterDashboard.propTypes = {};

export default MatterDashboard;
